// ==========================================================================
// Push Notifications Hook - Manages FCM token lifecycle and foreground messages
// ==========================================================================

import { useEffect, useRef, useCallback } from 'react';
import { requestFcmToken, onForegroundMessage } from '@/lib/firebase';
import { registerPushToken } from '@/services/pushSubscription.service';

const TOKEN_STORAGE_KEY_PREFIX = 'fcm_token:';

/**
 * Hook to manage push notification registration.
 * - Requests notification permission
 * - Gets FCM token and registers with backend
 * - Listens for foreground messages
 *
 * Token is registered once per (user, token) pair. Changing users on the same browser
 * re-registers the token under the new user.
 */
export function usePushNotifications(userId: number | undefined) {
  const registeredRef = useRef<number | null>(null);

  const registerToken = useCallback(async (uid: number) => {
    if (registeredRef.current === uid) return;

    try {
      const token = await requestFcmToken();
      if (!token) return;

      const storageKey = `${TOKEN_STORAGE_KEY_PREFIX}${uid}`;
      const previousToken = localStorage.getItem(storageKey);
      if (token !== previousToken) {
        await registerPushToken(token);
        localStorage.setItem(storageKey, token);
      }

      registeredRef.current = uid;
    } catch (error) {
      console.warn('Push notification registration failed:', error);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

    registerToken(userId);
  }, [userId, registerToken]);

  useEffect(() => {
    if (!userId) return;

    // Foreground push handler: relay to the system tray only — no in-app
    // toast. When the PWA is open and a push arrives we don't want a
    // banner in the UI, but we still need to surface it in the Android
    // tray (Firebase doesn't auto-display data-only payloads).
    const unsubscribe = onForegroundMessage((payload: unknown) => {
      const msg = payload as {
        data?: {
          title?: string;
          body?: string;
          image?: string;
          url?: string;
          tag?: string;
          _track_url?: string;
          _analytics_label?: string;
          _push_id?: string;
        };
      };
      const data = msg.data || {};
      const title = data.title;
      const body = data.body;

      // Push Received is tracked by firebase-messaging-sw.js exclusively.
      // The FCM JS SDK fires `onBackgroundMessage` for every data-only push
      // regardless of foreground/background state, so the SW already POSTs
      // exactly once per push received. Firing Push Received from here too
      // would double-count every push that arrives while the tab is open.

      if (!title && !body) return;

      if (!('serviceWorker' in navigator) || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }

      navigator.serviceWorker
        .getRegistration('/firebase-cloud-messaging-push-scope')
        .then((reg) => reg ?? navigator.serviceWorker.ready)
        .then((registration) => {
          registration.showNotification(title || 'Yaya', {
            body: body || '',
            icon: '/atram_icon.png',
            badge: '/atram_icon.png',
            tag: data.tag || undefined,
            requireInteraction: true,
            // Preserve the tracking metadata so notificationclick can fire
            // Push Opened from the same handler the SW already uses.
            data: {
              url: data.url || '/',
              _track_url: data._track_url,
              _analytics_label: data._analytics_label,
              _push_id: data._push_id,
              tag: data.tag,
            },
            ...(data.image ? { image: data.image } : {}),
            vibrate: [200, 100, 200],
          } as NotificationOptions);
        })
        .catch((err) => console.warn('[push] foreground showNotification failed:', err));
    });

    return unsubscribe;
  }, [userId]);
}
